import React, { FunctionComponent, useState } from "react";
import { Button } from "../Button";
import {
  Typography,
  MenuItem,
  SvgIcon,
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Stack,
  DialogActions,
  FormGroup,
  FormControlLabel,
  Checkbox,
  IconButton,
  Snackbar,
  Alert,
  Grid,
} from "@mui/material/";
import {
  mdiClose,
  mdiAccountCogOutline,
  mdiAccountDetailsOutline,
} from "@mdi/js";
import { Icon } from "@mdi/react";
import { CheckBox } from "@mui/icons-material";
import { CustomTabs } from "../CustomTabs";
import { IProps } from "./IProps";
import { CustomSelect } from "../CustomSelect";
import { getAxios } from "../../utils/endpoints";
import { useAssignDialog } from "./hook";
import { PageLevelSecurity } from "../../types/PageLevelSecurity";

type SelectionListItem = {
  id: number;
  name: string;
  checked: boolean;
  searchVal: string;
};

type TabName = "pageLevel" | "functionLevel";

interface ISelectionListProps {
  name: TabName;
  items: SelectionListItem[];
  selectedItems: number;
  onChangeParent: (
    to: TabName,
    event: React.ChangeEvent<HTMLInputElement>
  ) => void;
  onChange: (to: TabName, event: React.ChangeEvent<HTMLInputElement>) => void;
  onSelectAllItemsByFilter: (
    to: TabName,
    filterName: string,
    event: React.ChangeEvent<HTMLInputElement>
  ) => void;
}

const SelectionList: FunctionComponent<ISelectionListProps> = (
  props
): JSX.Element => {
  const [search, setSearch] = useState<string>("");

  const menuItems = props.items.map((val) => val.searchVal);

  const filtered = menuItems.filter(
    (val, index) => menuItems.indexOf(val) === index
  );

  const allItemsAreSelected: boolean =
    props.items.length > 0 && props.items.every((val) => val.checked === true);

  return (
    <>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        borderBottom="2px solid #0000001F"
        paddingBottom={2}
      >
        <CustomSelect
          value={search}
          name="search"
          sx={{ width: 200, height: 35 }}
          label=""
          displayEmpty
          renderValue={(selected) => {
            if (!selected) {
              return "All";
            }
            return selected as string;
          }}
          onChange={(event) => {
            setSearch(event.target.value as string);
          }}
        >
          <MenuItem value="">
            <em>All</em>
          </MenuItem>
          {filtered.map((item) => (
            <MenuItem key={item} value={item}>
              {item}
            </MenuItem>
          ))}
        </CustomSelect>
      </Box>

      <FormGroup>
        <FormControlLabel
          control={
            <Checkbox
              checked={props.selectedItems === props.items.length}
              onChange={(event) =>
                search
                  ? props.onSelectAllItemsByFilter(props.name, search, event)
                  : props.onChangeParent(props.name, event)
              }
            />
          }
          label="Select All"
        />

        <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 2, md: 3 }}>
          {props.items
            .filter((val) => {
              if (!search) return true;
              return val.searchVal.includes(search);
            })
            .map((val) => (
              <Grid item xs={6} key={val.id}>
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={val.checked}
                        value={val.id}
                        onChange={(event) => props.onChange(props.name, event)}
                      />
                    }
                    label={val.name}
                  />
                </FormGroup>
              </Grid>
            ))}
        </Grid>
      </FormGroup>
    </>
  );
};

export const AssignDialog: FunctionComponent<IProps> = (props): JSX.Element => {
  const {
    functionLevelSecurityItems,
    pageLevelSecurityItems,
    functionLevelIds,
    pageLevelIds,
    snackbarMessage,
    snackbarType,
    setSnackbarMessage,
    setSnackbarType,
    onChange,
    onChangeParent,
    onSave,
    onSelectAllItemsByFilter,
  } = useAssignDialog(props.title, props.onCancel!, props.onContinue!);

  const pageLevelItems: SelectionListItem[] = pageLevelSecurityItems.map(
    (val) => ({
      checked: val.hasAccess,
      name: val.description,
      id: val.menuId,
      searchVal: val.sectionDescription,
    })
  );

  const functionLevelItems: SelectionListItem[] =
    functionLevelSecurityItems.map((val) => ({
      checked: val.hasAccess,
      id: val.accessId,
      name: val.itemName,
      searchVal: val.pageName,
    }));

  return (
    <Dialog
      open={props.open}
      PaperProps={{
        style: {
          width: 600,
          height: 831,
          borderRadius: 8,
        },
      }}
    >
      <DialogTitle
        display="flex"
        alignItems="center"
        justifyContent="space-between"
      >
        <Stack direction="row" alignItems="center" spacing={2}>
          <SvgIcon>
            <Icon
              path={mdiAccountDetailsOutline}
              style={{ fontSize: 23, color: "#1BA0E9" }}
            />
          </SvgIcon>
          <Typography fontSize={22} fontWeight={700}>
            {props.title}
          </Typography>
        </Stack>
        <IconButton onClick={props.onCancel}>
          <SvgIcon>
            <Icon path={mdiClose} />
          </SvgIcon>
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <CustomTabs
          tabLabels={[
            { title: "Page Level Security" },
            { title: "Function Level Security" },
          ]}
          tabComponents={[
            <SelectionList
              name="pageLevel"
              onChangeParent={onChangeParent}
              onChange={onChange}
              items={pageLevelItems}
              selectedItems={pageLevelIds.length}
              onSelectAllItemsByFilter={onSelectAllItemsByFilter}
            />,
            <SelectionList
              name="functionLevel"
              onChangeParent={onChangeParent}
              onChange={onChange}
              items={functionLevelItems}
              selectedItems={functionLevelIds.length}
              onSelectAllItemsByFilter={onSelectAllItemsByFilter}
            />,
          ]}
        />
      </DialogContent>

      <DialogActions sx={{ borderTop: "2px solid #0000001F" }}>
        <Button label="Save" onClick={onSave} />
        <Button label="Cancel" variant="outlined" onClick={props.onCancel} />
      </DialogActions>

      <Snackbar
        open={Boolean(snackbarMessage.length)}
        autoHideDuration={3000}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        onClose={() => {
          setSnackbarMessage("");
          setSnackbarType("info");
        }}
      >
        <Alert severity={snackbarType}>{snackbarMessage}</Alert>
      </Snackbar>
    </Dialog>
  );
};